import { useCallback, useMemo } from 'react'
import { InputNumber } from 'antd'
import { useTranslation } from 'react-i18next'
import { currentIntlLocale } from '@/app/i18n'
import { currencySymbol, getCurrencyCode } from '@/shared/lib/format'
import {
  currencyExponent,
  majorToMinor,
  minorToMajor,
  type Money,
} from '@/shared/lib/money'

export interface MoneyInputProps {
  /** antd `Form.Item` avtomatik beradi. Qiymat MINOR birlikda. */
  value?: Money | null
  onChange?: (value: Money | null) => void
  /** Tenant valyutasidan boshqasi kerak bo'lsa */
  currency?: string
  /** Chegara MINOR birlikda — chaqiruvchi ikki xil birlik bilan ishlamasin */
  minMinor?: number
  maxMinor?: number
  disabled?: boolean
  placeholder?: string
  autoFocus?: boolean
  size?: 'small' | 'middle' | 'large'
  style?: React.CSSProperties
}

/**
 * Pul kiritish maydoni.
 *
 * IKKI BIRLIK CHEGARASI SHU YERDA VA FAQAT SHU YERDA:
 *   · tashqi dunyo (props, `onChange`, backend) — MINOR birlik, butun son
 *   · ekrandagi maydon — MAJOR birlik, odam o'qiydigan son
 *
 * Nega shunday: aylantirish har formada takrorlansa, birontasida `Math.round`
 * unutiladi va 19.99 * 100 = 1998.9999999999998 serverga butun son sifatida
 * ketadi (`money.ts` dagi izoh). Bir joyda tursa — bir marta to'g'ri.
 */
export function MoneyInput({
  value,
  onChange,
  currency,
  minMinor,
  maxMinor,
  disabled,
  placeholder,
  autoFocus,
  size = 'middle',
  style,
}: MoneyInputProps) {
  const { t } = useTranslation()

  // Valyuta uch manbadan: aniq berilgan → mavjud qiymatniki → tenant standarti
  const code = (currency ?? value?.currency ?? getCurrencyCode()).toUpperCase()
  const exponent = currencyExponent(code)
  const locale = currentIntlLocale()

  const groupFormatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: exponent,
      }),
    [locale, exponent],
  )

  /**
   * Guruh va kasr ajratgichlari — TAXMIN QILINMAYDI, locale'ning o'zidan
   * olinadi.
   *
   * ⚠️ NEGA BU MUHIM — jonli tizimda pul ming barobar kichrayardi.
   *
   * Ilgari `parser` shunday taxmin qilardi: "faqat vergul bo'lsa, u kasr
   * ajratgichi". Bu `ru`/`uz` da to'g'ri ishlardi, chunki u yerda guruh
   * ajratgichi — uzilmas probel. Lekin `en` da guruh ajratgichi VERGUL, va
   * `formatter` ning o'zi uni qo'yadi:
   *
   *     kiritildi  100000
   *     formatter  "100,000"      ← vergul GURUH ajratgichi
   *     parser     "100.000"      ← verguldan kasr yasadi
   *     natija     100            ← ming barobar kichik
   *
   * Ya'ni komponent o'z chiqishini noto'g'ri o'qirdi, va nuqson faqat
   * panel tili ingliz bo'lganda ko'rinardi — shuning uchun sinovlarda
   * o'tib ketgan. `formatToParts` ajratgichlarni AYNAN formatter
   * ishlatgan qiymatlar bilan beradi, ya'ni ikkalasi bir manbadan.
   */
  const { group, decimal } = useMemo(() => {
    const parts = groupFormatter.formatToParts(12345.6)
    return {
      group: parts.find((p) => p.type === 'group')?.value ?? '',
      decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
    }
  }, [groupFormatter])

  const majorValue = value ? minorToMajor(value.amount, code) : null

  const handleChange = useCallback(
    (next: number | string | null) => {
      if (next === null || next === '') {
        onChange?.(null)
        return
      }
      const asNumber = typeof next === 'number' ? next : Number(next)
      if (!Number.isFinite(asNumber)) {
        onChange?.(null)
        return
      }
      onChange?.({ amount: majorToMinor(asNumber, code), currency: code })
    },
    [onChange, code],
  )

  /**
   * Kiritilgan matnni songa aylantiradi.
   *
   * Ajratgichni QO'LDA olib tashlaymiz, chunki ru/uz locale'da guruh
   * ajratgichi — uzilmas probel (U+00A0). Foydalanuvchi uni klaviaturadan
   * TERA OLMAYDI, lekin maydondagi matnni nusxalab qaytadan qo'ysa aynan
   * shu belgi keladi. Uni tozalamasak `Number()` NaN qaytaradi va kiritilgan
   * summa jimgina yo'qoladi.
   */
  const parser = useCallback(
    (displayValue: string | undefined): string => {
      if (!displayValue) return ''
      // Raqam, ajratgichlar va minus qoladi; qolgani (valyuta belgisi,
      // uzilmas probel) tashlanadi.
      const cleaned = displayValue.replace(/[^\d,.\u00a0\u202f\s-]/g, '')
      // Guruh ajratgichlari YO'QOTILADI, kasr ajratgichi nuqtaga aylanadi.
      const withoutGroups = group ? cleaned.split(group).join('') : cleaned
      const normalized = decimal === '.' ? withoutGroups : withoutGroups.replace(decimal, '.')
      // Qolgan probellar — ba'zi locale'larda ajratgich sifatida keladi.
      return normalized.replace(/[\s\u00a0\u202f]/g, '')
    },
    [group, decimal],
  )

  const formatter = useCallback(
    (displayValue: string | number | undefined): string => {
      if (displayValue === undefined || displayValue === '') return ''
      const asNumber = typeof displayValue === 'number' ? displayValue : Number(displayValue)
      if (!Number.isFinite(asNumber)) return String(displayValue)
      return groupFormatter.format(asNumber)
    },
    [groupFormatter],
  )

  return (
    // Generik AYNIQSA `number` emas: `InputNumber<number>` da `parser` son
    // qaytarishi shart, ya'ni "12." yozilayotgan oraliq holat yo'qoladi va
    // kasr kiritish qiyinlashadi. Satr qaytaradigan parser bu holatni saqlaydi.
    <InputNumber
      value={majorValue}
      onChange={handleChange}
      formatter={formatter}
      parser={parser}
      precision={exponent}
      // Chegaralar minor'da berilgan — ekranga major'da qo'yiladi
      {...(minMinor === undefined ? { min: 0 } : { min: minorToMajor(minMinor, code) })}
      {...(maxMinor === undefined ? {} : { max: minorToMajor(maxMinor, code) })}
      disabled={disabled ?? false}
      placeholder={placeholder ?? t('money.placeholder')}
      autoFocus={autoFocus ?? false}
      size={size}
      style={{ width: '100%', ...style }}
      // Valyuta belgisi `suffix` orqali: u maydon bezagida turadi va
      // tahrirlanadigan matnga QO'SHILMAYDI, ya'ni `parser` uchun qo'shimcha
      // tozalash holati yaratmaydi.
      // (`addonAfter` antd v5.22 dan boshlab eskirgan — `Space.Compact`
      // tavsiya qilinadi, lekin bu yerda bezak kifoya.)
      suffix={currencySymbol(code, locale)}
    />
  )
}
